import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Rating from '@mui/material/Rating';

function ProductCard({ product }) {
  const {
    id,
    name,
    price,
    originalPrice,
    image,
    rating,
    reviewCount,
    badge,
    category,
  } = product;

  const discount = originalPrice
    ? Math.round(((originalPrice - price) / originalPrice) * 100)
    : 0;

  return (
    <Card
      component={RouterLink}
      to={`/product/${id}`}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        textDecoration: 'none',
        position: 'relative',
        bgcolor: 'background.paper',
        '&:hover': {
          '& .MuiCardMedia-root': {
            transform: 'scale(1.05)',
          },
        },
      }}
    >
      {/* Badge */}
      {badge && (
        <Chip
          label={badge}
          size="small"
          color={badge === 'Sale' ? 'error' : 'primary'}
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            zIndex: 1,
            fontWeight: 600,
            fontSize: '0.75rem',
          }}
        />
      )}

      {/* Image container */}
      <Box sx={{ overflow: 'hidden', bgcolor: '#f8f8f8' }}>
        <CardMedia
          component="img"
          height="280"
          image={image}
          alt={name}
          sx={{
            objectFit: 'cover',
            transition: 'transform 0.3s ease-in-out',
          }}
        />
      </Box>

      <CardContent sx={{ flexGrow: 1, pt: 2 }}>
        {/* Category */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
        >
          {category}
        </Typography>

        {/* Product name */}
        <Typography
          variant="h6"
          component="h3"
          sx={{
            mt: 0.5,
            mb: 1,
            fontWeight: 500,
            color: 'text.primary',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {name}
        </Typography>

        {/* Rating */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <Rating value={rating} precision={0.5} size="small" readOnly />
          <Typography variant="body2" color="text.secondary">
            ({reviewCount})
          </Typography>
        </Box>

        {/* Price */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="h6"
            component="span"
            sx={{ fontWeight: 600, color: 'primary.main' }}
          >
            ₹{price.toLocaleString()}
          </Typography>
          {originalPrice && (
            <>
              <Typography
                variant="body2"
                component="span"
                sx={{
                  textDecoration: 'line-through',
                  color: 'text.secondary',
                }}
              >
                ₹{originalPrice.toLocaleString()}
              </Typography>
              <Typography
                variant="body2"
                component="span"
                sx={{ color: 'error.main', fontWeight: 600 }}
              >
                {discount}% off
              </Typography>
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export default ProductCard;
